<template>
  <div class="modal-overlay" v-if="show" @click="closeModal">
    <div class="modal-content" @click.stop>
      <div class="modal-header">
        <h3>Export Call Statistics</h3>
        <button class="close-btn" @click="closeModal">
          <X />
        </button>
      </div>
      
      <div class="modal-body">
        <p>Export call and message data for analysis. Select a date range to export:</p>
        
        <div class="date-inputs">
          <div class="input-group">
            <label for="start-date">Start Date:</label>
            <input 
              type="date" 
              id="start-date" 
              v-model="startDate" 
              :max="endDate || today"
            />
          </div>
          
          <div class="input-group">
            <label for="end-date">End Date:</label>
            <input 
              type="date" 
              id="end-date" 
              v-model="endDate" 
              :min="startDate"
              :max="today"
            />
          </div>
        </div>
        
        <div class="date-presets">
          <button @click="setDateRange(7)" class="preset-btn">Last 7 days</button>
          <button @click="setDateRange(30)" class="preset-btn">Last 30 days</button>
          <button @click="setDateRange(90)" class="preset-btn">Last 3 months</button>
        </div>
        
        <div class="export-info">
          <h4>Export will include:</h4>
          <ul>
            <li>All calls and message conversations in the selected date range</li>
            <li>Contact information (name, phone, email)</li>
            <li>Call duration and mode (inbound/outbound)</li>
            <li>Actions performed: bookings, cancellations, and reschedules</li>
            <li>Service names for bookings</li>
            <li>Full call transcript or compiled message conversation</li>
          </ul>
        </div>
      </div>
      
      <div class="modal-footer">
        <button @click="closeModal" class="cancel-btn">Cancel</button>
        <button 
          @click="exportData" 
          class="export-btn" 
          :disabled="!startDate || !endDate || isExporting"
        >
          <Download v-if="!isExporting" />
          <div v-else class="spinner"></div>
          {{ isExporting ? 'Exporting...' : 'Export CSV' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { X, Download } from 'lucide-vue-next';
import { exportStats } from '@/app/admin-routes';

export default {
  name: 'ExportModal',
  components: {
    X,
    Download
  },
  props: {
    show: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      startDate: '',
      endDate: '',
      isExporting: false,
      today: new Date().toISOString().split('T')[0]
    };
  },
  mounted() {
    // Set default to last 30 days
    this.setDateRange(30);
  },
  methods: {
    closeModal() {
      this.$emit('close');
    },
    
    setDateRange(days) {
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - days);
      
      this.endDate = end.toISOString().split('T')[0];
      this.startDate = start.toISOString().split('T')[0];
    },
    
    async exportData() {
      if (!this.startDate || !this.endDate) {
        alert('Please select both start and end dates.');
        return;
      }
      
      const startDateObj = new Date(this.startDate);
      const endDateObj = new Date(this.endDate);
      
      if (startDateObj > endDateObj) {
        alert('Start date must be before end date.');
        return;
      }
      
      this.isExporting = true;
      
      try {
        const result = await exportStats(this.startDate, this.endDate);
        
        if (result.success) {
          // File download should have started automatically
          this.closeModal();
        }
      } catch (error) {
        console.error('Export failed:', error);
        alert('Export failed. Please try again.');
      } finally {
        this.isExporting = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
@import "/src/assets/styles/variables.scss";

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 16px;
  border-bottom: 1px solid #e5e7eb;
  
  h3 {
    margin: 0;
    font-size: 20px;
    font-weight: 600;
    color: #111827;
  }
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  transition: all 0.2s;
  
  &:hover {
    background-color: #f3f4f6;
    color: #374151;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
}

.modal-body {
  padding: 24px;
  
  p {
    margin: 0 0 24px 0;
    color: #6b7280;
    line-height: 1.5;
  }
}

.date-inputs {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.input-group {
  display: flex;
  flex-direction: column;
  
  label {
    font-weight: 500;
    color: #374151;
    margin-bottom: 4px;
    font-size: 14px;
  }
  
  input {
    padding: 8px 12px;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 14px;
    transition: border-color 0.2s;
    
    &:focus {
      outline: none;
      border-color: $colourS;
      box-shadow: 0 0 0 3px rgba(37, 98, 190, 0.1);
    }
  }
}

.date-presets {
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  flex-wrap: wrap;
}

.preset-btn {
  padding: 6px 12px;
  background: #f3f4f6;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #e5e7eb;
    border-color: #9ca3af;
  }
}

.export-info {
  background: #f5f7fb;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  
  h4 {
    margin: 0 0 8px 0;
    font-size: 14px;
    font-weight: 600;
    color: #374151;
  }
  
  ul {
    margin: 0;
    padding-left: 16px;
    
    li {
      color: #6b7280;
      font-size: 13px;
      line-height: 1.4;
      margin-bottom: 4px;
      text-align: left;
      
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px 24px;
  border-top: 1px solid #e5e7eb;
}

.cancel-btn, .export-btn {
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 6px;
}

.cancel-btn {
  background: white;
  border: 1px solid #d1d5db;
  color: #374151;
  
  &:hover {
    background: #f5f7fb;
    border-color: #9ca3af;
  }
}

.export-btn {
  background: $colourS;
  border: 1px solid $colourS;
  color: white;
  
  &:hover:not(:disabled) {
    background: darken(#2562be, 5%);
    border-color: darken(#2562be, 5%);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
}

.spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

@media (max-width: 640px) {
  .modal-content {
    width: 95%;
    margin: 20px;
  }
  
  .date-inputs {
    grid-template-columns: 1fr;
  }
  
  .date-presets {
    justify-content: center;
  }
  
  .modal-footer {
    flex-direction: column-reverse;
    
    .cancel-btn, .export-btn {
      width: 100%;
      justify-content: center;
    }
  }
}
</style>
